// Admin Load Order planner: assign teams to planes/loads and publish team numbers.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { getDisplayName, abbreviateDays } from '../utils/flyerDisplay';
import './LoadOrder.css';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const LOAD_ORDER_EXPORT_COLS = [
  { id: 'plane', label: 'Plane', default: true },
  { id: 'load', label: 'Load', default: true },
  { id: 'slot', label: 'Slot', default: true },
  { id: 'teamNumber', label: 'Team #', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  { id: 'mentorEmail', label: 'Mentor email', default: false },
  { id: 'freshlings', label: 'Freshlings', default: true },
  { id: 'gear', label: 'Gear', default: true },
  { id: 'firstDay', label: 'First day', default: true },
  { id: 'takeoff', label: 'Takeoff ~', default: true },
];

const DEFAULT_PLANES = [
  { id: 'A', label: 'Plane A', firstTakeoff: '08:30' },
  { id: 'B', label: 'Plane B', firstTakeoff: '08:35' },
];

const LOAD_ORDER_PLANES_STORAGE_PREFIX = 'loadOrder.planes.';

function loadStoredPlanesForEvent(eventId) {
  if (!eventId) return [...DEFAULT_PLANES];
  try {
    const raw = localStorage.getItem(LOAD_ORDER_PLANES_STORAGE_PREFIX + eventId);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length >= 1) return parsed;
    }
  } catch {
    // ignore
  }
  return [...DEFAULT_PLANES];
}

const DAY_ORDER = ['wed', 'thu', 'fri', 'sat', 'sun'];

function firstDayInfo(daysStr) {
  if (!daysStr || typeof daysStr !== 'string') return { code: null, index: null };
  const lower = daysStr.toLowerCase();
  let bestIndex = null;
  DAY_ORDER.forEach((d, idx) => {
    if (lower.includes(d) && bestIndex == null) {
      bestIndex = idx;
    }
  });
  if (bestIndex == null) return { code: null, index: null };
  const codeMap = ['W', 'R', 'F', 'Sa', 'Su'];
  return { code: codeMap[bestIndex], index: bestIndex };
}

function needsGear(freshling) {
  const raw = (freshling && freshling.gsg != null ? String(freshling.gsg) : '').trim().toLowerCase();
  if (!raw) return false;
  if (raw === 'no' || raw === 'n' || raw === '0') return false;
  return true;
}

function getFreshlingSummary(team) {
  if (!team || !Array.isArray(team.freshlingFlyers) || !team.freshlingFlyers.length) {
    return '';
  }
  const names = team.freshlingFlyers.map(
    (f) => getDisplayName(f) || f.nickname || f.firstName || f.email || 'Freshling'
  );
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]}, ${names[1]}`;
  }
  return `${names[0]}, ${names[1]} +${names.length - 2}`;
}

function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map((x) => parseInt(x, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function minutesToTimeString(total) {
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

function LoadOrder() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [flyers, setFlyers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [planes, setPlanes] = useState(() => [...DEFAULT_PLANES]);
  const [loadDurationMinutes, setLoadDurationMinutes] = useState(20);
  const [teamsPerLoad, setTeamsPerLoad] = useState(7);
  const [teamsExpected, setTeamsExpected] = useState(55);

  const [assigningNumbers, setAssigningNumbers] = useState(false);
  /** Team id currently being dragged (HTML5 DnD). */
  const [draggedItem, setDraggedItem] = useState(null); // { teamId }
  const [expandedUnassigned, setExpandedUnassigned] = useState([]);
  const [expandedSlotted, setExpandedSlotted] = useState([]);
  const { emailComposerProps, openEmailComposer, closeEmailComposer } = useEmailComposer();
  const [notesCollapsed, setNotesCollapsed] = useState(true);
  const [notes, setNotes] = useState('');
  const [loadOrderTableExport, setLoadOrderTableExport] = useState(null); // { mode } | null
  const [loadOrderExportCols, setLoadOrderExportCols] = useState(() => new Set());

  /** Set to `'add' | 'remove'` before setPlanes so an effect can log committed `planes` after click. */
  const planesClickLogRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length) {
          setSelectedEventId((prev) =>
            prev && list.some((e) => e.id === prev)
              ? prev
              : resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: list })
          );
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load events');
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedEventId) return;
    setPlanes(loadStoredPlanesForEvent(selectedEventId));
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const [flyersSnap, teamsSnap] = await Promise.all([
          getDocs(
            query(
              collection(firestore, 'flyers'),
              where('eventId', '==', selectedEventId)
            )
          ),
          getDocs(
            query(
              collection(firestore, 'teams'),
              where('eventId', '==', selectedEventId)
            )
          ),
        ]);
        setFlyers(flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedEventId]);

  const notesStorageKey = `loadOrder_notes_${selectedEventId || 'none'}`;
  useEffect(() => {
    if (!selectedEventId) return;
    try {
      const stored = localStorage.getItem(notesStorageKey);
      setNotes(stored != null ? stored : '');
    } catch (_) {}
  }, [selectedEventId, notesStorageKey]);
  const saveNotes = (value) => {
    setNotes(value);
    try {
      localStorage.setItem(notesStorageKey, value);
    } catch (_) {}
  };

  const mentorsByEmail = useMemo(() => {
    const map = new Map();
    flyers
      .filter((f) => f.type === 'mentor')
      .forEach((m) => map.set((m.email || '').toLowerCase(), m));
    return map;
  }, [flyers]);

  const freshlingsByEmail = useMemo(() => {
    const map = new Map();
    flyers
      .filter((f) => f.type === 'freshling')
      .forEach((m) => map.set((m.email || '').toLowerCase(), m));
    return map;
  }, [flyers]);

  const enrichedTeams = useMemo(
    () =>
      teams.map((t) => {
        const mentor =
          t.mentor &&
          mentorsByEmail.get((t.mentor || '').toLowerCase());
        const freshlingFlyers = (t.freshlings || []).map(
          (e) => freshlingsByEmail.get((e || '').toLowerCase()) || { email: e }
        );
        return { ...t, mentorFlyer: mentor || null, freshlingFlyers };
      }),
    [teams, mentorsByEmail, freshlingsByEmail]
  );

  const unassignedTeams = useMemo(
    () =>
      enrichedTeams.filter(
        (t) => !t.planeId || t.planeLoadIndex == null || t.slotIndexInLoad == null
      ),
    [enrichedTeams]
  );

  const persistPlanes = (list) => {
    if (!selectedEventId) return;
    try {
      localStorage.setItem(LOAD_ORDER_PLANES_STORAGE_PREFIX + selectedEventId, JSON.stringify(list));
    } catch {
      // ignore quota / private mode
    }
  };

  const handleAddPlane = () => {
    console.log('[LoadOrder] Add plane — planes before click', planes);
    planesClickLogRef.current = 'add';
    setPlanes((prev) => {
      const used = new Set(prev.map((p) => String(p.id || '').trim().toUpperCase()));
      let nextId = null;
      for (const letter of 'CDEFGHIJKLMNOPQRSTUVWXYZ') {
        if (!used.has(letter)) {
          nextId = letter;
          break;
        }
      }
      if (!nextId) {
        planesClickLogRef.current = null;
        window.alert('No more plane letters available (A–Z).');
        return prev;
      }
      const last = prev[prev.length - 1];
      const lastMins = parseTimeToMinutes(last?.firstTakeoff);
      const nextTakeoff = lastMins != null ? minutesToTimeString(lastMins + 5) : '';
      const next = [
        ...prev,
        { id: nextId, label: `Plane ${nextId}`, firstTakeoff: nextTakeoff },
      ];
      persistPlanes(next);
      return next;
    });
  };

  const handleRemoveLastPlane = () => {
    console.log('[LoadOrder] Remove last plane — planes before click', planes);
    planesClickLogRef.current = 'remove';
    setPlanes((prev) => {
      if (prev.length <= 1) {
        planesClickLogRef.current = null;
        return prev;
      }
      const last = prev[prev.length - 1];
      const hasTeams = enrichedTeams.some((t) => t.planeId === last.id);
      if (hasTeams) {
        planesClickLogRef.current = null;
        window.alert(`Move teams off ${last.label || last.id} before removing this plane column.`);
        return prev;
      }
      const next = prev.slice(0, -1);
      persistPlanes(next);
      return next;
    });
  };

  useEffect(() => {
    const kind = planesClickLogRef.current;
    if (kind !== 'add' && kind !== 'remove') return;
    planesClickLogRef.current = null;
    console.log(
      kind === 'add'
        ? '[LoadOrder] Add plane — planes after click (committed)'
        : '[LoadOrder] Remove last plane — planes after click (committed)',
      planes
    );
  }, [planes]);

  const planeOrder = useMemo(
    () => planes.map((p) => p.id),
    [planes]
  );

  const maxLoadsPerPlane = useMemo(() => {
    const total = Math.max(0, parseInt(teamsExpected, 10) || 0);
    const perLoad = Math.max(1, teamsPerLoad);
    const numPlanes = Math.max(1, planes.length);
    const capacityPerRound = perLoad * numPlanes;
    return Math.max(1, Math.ceil(total / capacityPerRound));
  }, [teamsExpected, teamsPerLoad, planes.length]);

  const gridLoads = useMemo(() => {
    const result = [];
    if (!planes.length || !loadDurationMinutes) return result;
    for (let loadIndex = 1; loadIndex <= maxLoadsPerPlane; loadIndex++) {
      const row = {
        loadIndex,
        timesByPlane: {},
      };
      planes.forEach((plane) => {
        const base = parseTimeToMinutes(plane.firstTakeoff);
        if (base == null) {
          row.timesByPlane[plane.id] = '';
        } else {
          const t = base + (loadIndex - 1) * loadDurationMinutes;
          row.timesByPlane[plane.id] = minutesToTimeString(t);
        }
      });
      result.push(row);
    }
    return result;
  }, [planes, loadDurationMinutes, maxLoadsPerPlane]);

  const teamsBySlot = useMemo(() => {
    const map = {};
    for (const t of enrichedTeams) {
      if (!t.planeId || t.planeLoadIndex == null || t.slotIndexInLoad == null)
        continue;
      const key = `${t.planeId}:${t.planeLoadIndex}:${t.slotIndexInLoad}`;
      map[key] = t;
    }
    return map;
  }, [enrichedTeams]);

  const loadOrderAssignedExportRows = useMemo(() => {
    const assigned = enrichedTeams.filter(
      (t) =>
        t.planeId && t.planeLoadIndex != null && t.slotIndexInLoad != null
    );
    const planeIndex = new Map();
    planeOrder.forEach((id, idx) => planeIndex.set(id, idx));
    const sorted = [...assigned].sort((a, b) => {
      const ai = planeIndex.get(a.planeId) ?? 999;
      const bi = planeIndex.get(b.planeId) ?? 999;
      if (ai !== bi) return ai - bi;
      if (a.planeLoadIndex !== b.planeLoadIndex) {
        return a.planeLoadIndex - b.planeLoadIndex;
      }
      return a.slotIndexInLoad - b.slotIndexInLoad;
    });
    return sorted.map((t) => {
      const plane = planes.find((p) => p.id === t.planeId) || { id: t.planeId, label: t.planeId };
      let takeoffLabel = '';
      const baseMinutes = parseTimeToMinutes(plane.firstTakeoff);
      if (baseMinutes != null && t.planeLoadIndex != null) {
        const mins = baseMinutes + (t.planeLoadIndex - 1) * loadDurationMinutes;
        const h24 = Math.floor(mins / 60) % 24;
        const m = mins % 60;
        const ampm = h24 >= 12 ? 'pm' : 'am';
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        takeoffLabel = `~${h12}:${m.toString().padStart(2, '0')}${ampm}`;
      }
      const mentorEmail = (t.mentor || '').trim();
      const mentorName = t.mentorFlyer ? getDisplayName(t.mentorFlyer) : mentorEmail || '—';
      const fresh = t.freshlingFlyers || [];
      const gearCount = fresh.filter((f) => needsGear(f)).length;
      const firstDays =
        [...new Set(fresh.map((f) => firstDayInfo(f.daysAtBoogie).code).filter(Boolean))].join(', ') ||
        '—';
      return {
        plane: plane.label || plane.id,
        load: String(t.planeLoadIndex ?? ''),
        slot: String(t.slotIndexInLoad ?? ''),
        teamNumber: String(t.teamNumber ?? ''),
        mentor: mentorName,
        mentorEmail,
        freshlings: getFreshlingSummary(t),
        gear: gearCount > 0 ? `${gearCount} need gear` : '—',
        firstDay: firstDays,
        takeoff: takeoffLabel,
      };
    });
  }, [enrichedTeams, planeOrder, planes, loadDurationMinutes]);

  useEffect(() => {
    if (!loadOrderTableExport) return;
    setLoadOrderExportCols(
      new Set(LOAD_ORDER_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  }, [loadOrderTableExport]);

  const openLoadOrderTableExport = (mode) => {
    if (!loadOrderAssignedExportRows.length) {
      window.alert('No teams placed on planes yet. Assign teams to loads first.');
      return;
    }
    setLoadOrderTableExport({ mode });
  };

  const resetLoadOrderExportCols = () => {
    setLoadOrderExportCols(
      new Set(LOAD_ORDER_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runLoadOrderTableExport = (selectedColumnIds) => {
    if (!loadOrderTableExport) return;
    const { mode } = loadOrderTableExport;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : loadOrderExportCols;
    const active = LOAD_ORDER_EXPORT_COLS.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = loadOrderAssignedExportRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const ev = events.find((e) => e.id === selectedEventId) || null;
    const evTitle = ev?.title || selectedEventId || 'Load order';
    const sub = 'Assigned slots';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} team(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setLoadOrderTableExport(null);
  };

  const setTeamField = async (teamId, fields) => {
    await updateDoc(doc(firestore, 'teams', teamId), fields);
    setTeams((prev) =>
      prev.map((t) => (t.id === teamId ? { ...t, ...fields } : t))
    );
  };

  const handleEmailSent = async ({ selectedTemplate, context: emailContext }) => {
    if (!emailContext) return;
    try {
      if (selectedTemplate === 'early_load_warning') {
        if (emailContext.teamId) {
          await setTeamField(emailContext.teamId, {
            earlyLoadWarningSentAt: serverTimestamp(),
          });
        }
        if (Array.isArray(emailContext.teamIds)) {
          for (const id of emailContext.teamIds) {
            await setTeamField(id, {
              earlyLoadWarningSentAt: serverTimestamp(),
            });
          }
        }
      }
      if (selectedTemplate === 'mentor_load_summary') {
        if (emailContext.teamId) {
          await setTeamField(emailContext.teamId, {
            mentorLoadSummarySentAt: serverTimestamp(),
          });
        }
        if (Array.isArray(emailContext.teamIds)) {
          for (const id of emailContext.teamIds) {
            await setTeamField(id, {
              mentorLoadSummarySentAt: serverTimestamp(),
            });
          }
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to update email status.');
    }
  };

  const handleTeamEarlyWarning = (team) => {
    const mentorEmail =
      (team.mentor && String(team.mentor).trim()) ||
      (team.mentorFlyer && team.mentorFlyer.email && String(team.mentorFlyer.email).trim()) ||
      '';
    if (!mentorEmail) {
      window.alert('No mentor email for this team.');
      return;
    }
    const event = events.find((e) => e.id === selectedEventId) || null;
    openEmailComposer({
      template: 'early_load_warning',
      to: mentorEmail,
      bcc: '',
      context: {
        type: 'mentor',
        team,
        teamId: team.id,
        event,
      },
    });
  };

  const handlePlaneEarlyWarning = (planeId) => {
    const teamsOnPlane = enrichedTeams.filter((t) => t.planeId === planeId);
    if (!teamsOnPlane.length) {
      window.alert('No teams assigned to this plane yet.');
      return;
    }
    const mentorEmailSet = new Map();
    const plane = planes.find((p) => p.id === planeId) || { id: planeId, label: planeId };
    const baseMinutes = parseTimeToMinutes(plane.firstTakeoff);
    const planeSummaryRows = [];
    teamsOnPlane.forEach((t) => {
      const mentorEmail =
        (t.mentor && String(t.mentor).trim()) ||
        (t.mentorFlyer && t.mentorFlyer.email && String(t.mentorFlyer.email).trim()) ||
        '';
      if (mentorEmail) {
        const key = mentorEmail.toLowerCase();
        if (!mentorEmailSet.has(key)) {
          mentorEmailSet.set(key, mentorEmail);
        }
      }
      const mentorName = t.mentorFlyer ? getDisplayName(t.mentorFlyer) : (t.mentor || 'No mentor');
      let takeoffLabel = '';
      if (baseMinutes != null && t.planeLoadIndex != null) {
        const mins = baseMinutes + (t.planeLoadIndex - 1) * loadDurationMinutes;
        const h24 = Math.floor(mins / 60) % 24;
        const m = mins % 60;
        const ampm = h24 >= 12 ? 'pm' : 'am';
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        takeoffLabel = `~${h12}:${m.toString().padStart(2, '0')}${ampm}`;
      }
      planeSummaryRows.push({
        mentorName,
        teamNumber: t.teamNumber || '',
        planeLabel: plane.label,
        takeoffLabel,
      });
    });
    const mentorEmails = Array.from(mentorEmailSet.values());
    if (!mentorEmails.length) {
      window.alert('No mentor emails found for this plane.');
      return;
    }
    const event = events.find((e) => e.id === selectedEventId) || null;
    openEmailComposer({
      template: 'mentor_load_summary',
      to: '',
      bcc: mentorEmails.join(','),
      context: {
        type: 'plane',
        planeId,
        teamIds: teamsOnPlane.map((t) => t.id),
        event,
        planeSummaryRows,
      },
    });
  };

  const handleResetEarlyLoadWarnings = async () => {
    const withFlag = teams.filter((t) => t.earlyLoadWarningSentAt);
    if (!withFlag.length) {
      window.alert('No teams have the early load warning set.');
      return;
    }
    if (!window.confirm(`Clear "early load warning sent" for ${withFlag.length} team(s)? (For testing only.)`)) {
      return;
    }
    setError('');
    try {
      for (const t of withFlag) {
        await updateDoc(doc(firestore, 'teams', t.id), {
          earlyLoadWarningSentAt: deleteField(),
        });
      }
      setTeams((prev) =>
        prev.map((t) =>
          t.earlyLoadWarningSentAt ? { ...t, earlyLoadWarningSentAt: undefined } : t
        )
      );
    } catch (e) {
      setError(e.message || 'Failed to reset early load warnings.');
    }
  };

  const handleAllMentorsLoadSummary = () => {
    const assignedTeams = enrichedTeams.filter(
      (t) =>
        t.planeId &&
        t.planeLoadIndex != null &&
        t.slotIndexInLoad != null
    );
    if (!assignedTeams.length) {
      window.alert('No teams placed on planes yet. Assign teams to loads first.');
      return;
    }
    const planeIndex = new Map();
    planeOrder.forEach((id, idx) => planeIndex.set(id, idx));
    const sorted = [...assignedTeams].sort((a, b) => {
      const ai = planeIndex.get(a.planeId) ?? 999;
      const bi = planeIndex.get(b.planeId) ?? 999;
      if (ai !== bi) return ai - bi;
      if (a.planeLoadIndex !== b.planeLoadIndex) {
        return a.planeLoadIndex - b.planeLoadIndex;
      }
      return a.slotIndexInLoad - b.slotIndexInLoad;
    });
    const mentorEmailSet = new Map();
    const planeSummaryRows = [];
    sorted.forEach((t) => {
      const mentorEmail =
        (t.mentor && String(t.mentor).trim()) ||
        (t.mentorFlyer && t.mentorFlyer.email && String(t.mentorFlyer.email).trim()) ||
        '';
      if (mentorEmail) {
        const key = mentorEmail.toLowerCase();
        if (!mentorEmailSet.has(key)) {
          mentorEmailSet.set(key, mentorEmail);
        }
      }
      const plane = planes.find((p) => p.id === t.planeId) || { id: t.planeId, label: t.planeId };
      const mentorName = t.mentorFlyer ? getDisplayName(t.mentorFlyer) : (t.mentor || 'No mentor');
      let takeoffLabel = '';
      const baseMinutes = parseTimeToMinutes(plane.firstTakeoff);
      if (baseMinutes != null && t.planeLoadIndex != null) {
        const mins = baseMinutes + (t.planeLoadIndex - 1) * loadDurationMinutes;
        const h24 = Math.floor(mins / 60) % 24;
        const m = mins % 60;
        const ampm = h24 >= 12 ? 'pm' : 'am';
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        takeoffLabel = `~${h12}:${m.toString().padStart(2, '0')}${ampm}`;
      }
      planeSummaryRows.push({
        mentorName,
        teamNumber: t.teamNumber || '',
        planeLabel: plane.label || plane.id,
        takeoffLabel,
      });
    });
    const mentorEmails = Array.from(mentorEmailSet.values());
    if (!mentorEmails.length) {
      window.alert('No mentor emails found for assigned teams.');
      return;
    }
    const event = events.find((e) => e.id === selectedEventId) || null;
    openEmailComposer({
      template: 'mentor_load_summary',
      to: '',
      bcc: mentorEmails.join(','),
      context: {
        type: 'all',
        teamIds: sorted.map((t) => t.id),
        event,
        planeSummaryRows,
      },
    });
  };

  const handleResetLoadOrder = async () => {
    const assigned = teams.filter(
      (t) =>
        t.planeId ||
        t.planeLoadIndex != null ||
        t.slotIndexInLoad != null
    );
    if (!assigned.length) {
      window.alert('No teams are currently placed on planes/loads.');
      return;
    }
    if (
      !window.confirm(
        `Move ${assigned.length} team(s) off the planes back to "Unassigned"?`
      )
    ) {
      return;
    }
    setError('');
    try {
      for (const t of assigned) {
        await updateDoc(doc(firestore, 'teams', t.id), {
          planeId: null,
          planeLoadIndex: null,
          slotIndexInLoad: null,
        });
      }
      setTeams((prev) =>
        prev.map((t) =>
          t.planeId ||
          t.planeLoadIndex != null ||
          t.slotIndexInLoad != null
            ? {
                ...t,
                planeId: null,
                planeLoadIndex: null,
                slotIndexInLoad: null,
              }
            : t
        )
      );
    } catch (e) {
      setError(e.message || 'Failed to reset load order.');
    }
  };

  const handleSlotDragOver = (e) => {
    if (!draggedItem?.teamId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSlot = async (planeId, loadIndex, slotIndex) => {
    if (!draggedItem?.teamId) return;
    const teamId = draggedItem.teamId;
    const draggedTeam = teams.find((t) => t.id === teamId);
    if (!draggedTeam) {
      setDraggedItem(null);
      return;
    }
    const targetTeam = teams.find(
      (t) =>
        t.id !== teamId &&
        t.planeId === planeId &&
        t.planeLoadIndex === loadIndex &&
        t.slotIndexInLoad === slotIndex
    );
    const sourcePlaneId = draggedTeam.planeId || null;
    const sourceLoadIndex =
      draggedTeam.planeLoadIndex != null ? draggedTeam.planeLoadIndex : null;
    const sourceSlotIndex =
      draggedTeam.slotIndexInLoad != null ? draggedTeam.slotIndexInLoad : null;
    setDraggedItem(null);
    if (
      sourcePlaneId === planeId &&
      sourceLoadIndex === loadIndex &&
      sourceSlotIndex === slotIndex
    ) {
      return;
    }
    try {
      await setTeamField(teamId, {
        planeId,
        planeLoadIndex: loadIndex,
        slotIndexInLoad: slotIndex,
      });
      if (targetTeam) {
        if (
          sourcePlaneId &&
          sourceLoadIndex != null &&
          sourceSlotIndex != null
        ) {
          await setTeamField(targetTeam.id, {
            planeId: sourcePlaneId,
            planeLoadIndex: sourceLoadIndex,
            slotIndexInLoad: sourceSlotIndex,
          });
        } else {
          await setTeamField(targetTeam.id, {
            planeId: null,
            planeLoadIndex: null,
            slotIndexInLoad: null,
          });
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to move team.');
    }
  };

  const handleRemoveFromSlot = async (teamId) => {
    const choice = window.prompt(
      'Type 1 to leave a hole, or 2 to move everyone on this plane up.\n(Anything else cancels.)'
    );
    if (choice !== '1' && choice !== '2') return;
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    const { planeId, planeLoadIndex, slotIndexInLoad } = team;
    if (!planeId || planeLoadIndex == null || slotIndexInLoad == null) {
      await setTeamField(teamId, {
        planeId: null,
        planeLoadIndex: null,
        slotIndexInLoad: null,
      });
      return;
    }
    if (choice === '1') {
      await setTeamField(teamId, {
        planeId: null,
        planeLoadIndex: null,
        slotIndexInLoad: null,
      });
      return;
    }
    try {
      const samePlane = teams.filter((t) => t.planeId === planeId);
      const affected = samePlane.filter(
        (t) =>
          t.id !== teamId &&
          ((t.planeLoadIndex === planeLoadIndex &&
            t.slotIndexInLoad > slotIndexInLoad) ||
            t.planeLoadIndex > planeLoadIndex)
      );
      await setTeamField(teamId, {
        planeId: null,
        planeLoadIndex: null,
        slotIndexInLoad: null,
      });
      for (const t of affected) {
        const isSameLoad =
          t.planeLoadIndex === planeLoadIndex &&
          t.slotIndexInLoad > slotIndexInLoad;
        await setTeamField(t.id, {
          planeId,
          planeLoadIndex: isSameLoad
            ? t.planeLoadIndex
            : t.planeLoadIndex - 1,
          slotIndexInLoad: isSameLoad
            ? t.slotIndexInLoad - 1
            : t.slotIndexInLoad,
        });
      }
    } catch (e) {
      setError(e.message || 'Failed to move teams after removal.');
    }
  };

  const handleAssignTeamNumbersFromLoadOrder = async () => {
    if (!planeOrder.length) return;
    const assigned = enrichedTeams.filter(
      (t) =>
      t.planeId &&
      t.planeLoadIndex != null &&
      t.slotIndexInLoad != null
    );
    if (!assigned.length) {
      window.alert('No teams placed on planes/loads to assign numbers.');
      return;
    }
    const anyExisting = enrichedTeams.some(
      (t) => String(t.teamNumber || '').trim().length > 0
    );
    if (anyExisting) {
      const ok = window.confirm(
        'Some teams already have public team numbers. Reassign numbers for all placed teams based on this load order?'
      );
      if (!ok) return;
    }
    const sorted = [...assigned].sort((a, b) => {
      // Order by load (time slice) first, then plane within that load, then slot.
      if (a.planeLoadIndex !== b.planeLoadIndex) {
        return a.planeLoadIndex - b.planeLoadIndex;
      }
      const ai = planeOrder.indexOf(a.planeId);
      const bi = planeOrder.indexOf(b.planeId);
      const aPlaneIdx = ai === -1 ? Number.POSITIVE_INFINITY : ai;
      const bPlaneIdx = bi === -1 ? Number.POSITIVE_INFINITY : bi;
      if (aPlaneIdx !== bPlaneIdx) {
        return aPlaneIdx - bPlaneIdx;
      }
      return a.slotIndexInLoad - b.slotIndexInLoad;
    });
    setAssigningNumbers(true);
    try {
      let num = 1;
      for (const t of sorted) {
        await setTeamField(t.id, { teamNumber: String(num++) });
      }
      const unplacedWithNumbers = enrichedTeams.filter(
        (t) =>
          (!t.planeId ||
            t.planeLoadIndex == null ||
            t.slotIndexInLoad == null) &&
          String(t.teamNumber || '').trim().length > 0
      );
      for (const t of unplacedWithNumbers) {
        await setTeamField(t.id, { teamNumber: null });
      }
    } catch (e) {
      setError(e.message || 'Failed to assign team numbers.');
    } finally {
      setAssigningNumbers(false);
    }
  };

  const handleInlineNumberChange = async (teamId, raw) => {
    const value = (raw || '').trim();
    const num = value || null;
    try {
      await setTeamField(teamId, { teamNumber: num });
    } catch (e) {
      setError(e.message || 'Failed to update team number.');
    }
  };

  if (loading && !events.length) {
    return (
      <div className="load-order">
        <h2>Load Order</h2>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="load-order">
      <div className="load-order-header">
        <h2>Load Order</h2>
        <div>
          <label>
            Event:{' '}
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <div className="load-order-scope-banner" role="status">
        Load order applies to all rounds
      </div>
      {error && <p className="team-pairing-error">{error}</p>}
      <div className="load-order-notes">
        <button
          type="button"
          className="load-order-notes-toggle"
          onClick={() => setNotesCollapsed((c) => !c)}
          aria-expanded={!notesCollapsed}
        >
          <span className="load-order-notes-toggle-icon">
            {notesCollapsed ? '▶' : '▼'}
          </span>
          Notes
        </button>
        {!notesCollapsed && (
          <textarea
            className="load-order-notes-textarea"
            placeholder="Manual notes for this load order…"
            value={notes}
            onChange={(e) => saveNotes(e.target.value)}
            rows={4}
          />
        )}
      </div>
      <div className="load-order-toolbar">
        <label>
          Load duration (min):{' '}
          <input
            type="number"
            min="5"
            value={loadDurationMinutes}
            onChange={(e) =>
              setLoadDurationMinutes(
                Math.max(1, parseInt(e.target.value || '0', 10) || 1)
              )
            }
            style={{ width: 60 }}
          />
        </label>
        <label>
          Teams per load:{' '}
          <input
            type="number"
            min="1"
            value={teamsPerLoad}
            onChange={(e) =>
              setTeamsPerLoad(
                Math.max(1, parseInt(e.target.value || '0', 10) || 1)
              )
            }
            style={{ width: 60 }}
          />
        </label>
        <label>
          Teams expected:{' '}
          <input
            type="number"
            min="0"
            value={teamsExpected}
            onChange={(e) =>
              setTeamsExpected(
                Math.max(0, parseInt(e.target.value || '0', 10) || 0)
              )
            }
            style={{ width: 60 }}
          />
        </label>
        <span className="load-order-computed">
          Loads per plane: <strong>{maxLoadsPerPlane}</strong>
        </span>
        <button
          type="button"
          className="load-order-add-plane-btn"
          onClick={handleAddPlane}
          title="Add another plane column (C, D, …). Saved for this event in this browser."
        >
          Add plane
        </button>
        <button
          type="button"
          className="load-order-remove-plane-btn"
          onClick={handleRemoveLastPlane}
          disabled={planes.length <= 1}
          title="Remove the rightmost plane column if it has no teams assigned"
        >
          Remove last plane
        </button>
        <button
          type="button"
          className="load-order-all-email-btn"
          title="Send tomorrow's details (team numbers + planes) to all mentors on the load order"
          onClick={handleAllMentorsLoadSummary}
        >
          Email all mentors (night before)
        </button>
        <button
          type="button"
          className="load-order-export-print-btn"
          onClick={() => openLoadOrderTableExport('print')}
          disabled={!loadOrderAssignedExportRows.length}
        >
          Print table
        </button>
        <AdminCopyMenu
          ariaLabel="Copy load order table"
          disabled={!loadOrderAssignedExportRows.length}
          items={[
            {
              label: 'Copy table…',
              onSelect: () => openLoadOrderTableExport('copy'),
              showCopiedFeedback: false,
            },
          ]}
        />
        <button
          type="button"
          className="load-order-reset-loadorder-btn"
          onClick={handleResetLoadOrder}
          title="Move everyone off the planes back to Unassigned"
        >
          Clear load
        </button>
        <button
          type="button"
          className="load-order-reset-early-btn"
          onClick={handleResetEarlyLoadWarnings}
          title="Clear early load warning sent for all teams (testing)"
        >
          Reset early load warnings
        </button>
      </div>
      <div className="load-order-main">
        <section className="load-order-unassigned">
          <h3>Unassigned teams ({unassignedTeams.length})</h3>
          <div className="load-order-unassigned-list">
            {unassignedTeams.map((t) => {
              const m = t.mentorFlyer;
              const fresh = t.freshlingFlyers || [];
              const gearCount = fresh.filter((f) => needsGear(f)).length;
              const isExpanded = expandedUnassigned.includes(t.id);
              return (
                <div
                  key={t.id}
                  className={
                    'load-order-team-pill' +
                    (gearCount === 0 ? ' load-order-team-pill--no-gear' : '')
                  }
                  draggable
                  onDragStart={(e) => {
                    setDraggedItem({ teamId: t.id });
                    e.dataTransfer.setData('text/plain', t.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={() => setDraggedItem(null)}
                  onClick={() =>
                    setExpandedUnassigned((prev) =>
                      prev.includes(t.id)
                        ? prev.filter((id) => id !== t.id)
                        : [...prev, t.id]
                    )
                  }
                >
                  <div className="load-order-team-pill-main">
                    {t.teamNumber ? `#${t.teamNumber} – ` : ''}
                    {m ? getDisplayName(m) : 'No mentor'}
                    {m && m.email && (
                      <button
                        type="button"
                        className="load-order-email-btn"
                        draggable={false}
                        title="Early load email to mentor"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTeamEarlyWarning(t);
                        }}
                      >
                        ✉
                      </button>
                    )}
                  </div>
                  <div className="load-order-team-pill-meta">
                    {fresh.length > 0 && (
                      <span
                        className={
                          'load-order-tag ' +
                          (gearCount === 0
                            ? 'load-order-tag-gear-ok'
                            : 'load-order-tag-gear-some')
                        }
                      >
                        Gear needed: {gearCount} / {fresh.length}
                      </span>
                    )}
                    {fresh.map((f, idx) => {
                      const { code, index } = firstDayInfo(f.days);
                      const late = index === 3; // Saturday first
                      return (
                        <span
                          key={f.email || idx}
                          className={
                            'load-order-tag-fresh ' +
                            (late ? 'load-order-tag-fresh-late' : '')
                          }
                          title={abbreviateDays(f.days) || ''}
                        >
                          {f.homeDz || 'Home DZ ?'} · {code || '?'}
                          {needsGear(f) ? ' · Gear' : ''}
                        </span>
                      );
                    })}
                  </div>
                  {isExpanded && fresh.length > 0 && (
                    <div className="load-order-team-pill-details">
                      {fresh.map((f, idx) => {
                        const { code } = firstDayInfo(f.days);
                        return (
                          <div
                            key={f.email || idx}
                            className="load-order-team-pill-detail-row"
                          >
                            <div className="load-order-team-pill-detail-name">
                              {getDisplayName(f) || f.email || 'Freshling'}
                            </div>
                            <div className="load-order-team-pill-detail-meta">
                              <span>{f.homeDz || 'Home DZ ?'}</span>
                              <span>{abbreviateDays(f.days) || 'Days ?'}</span>
                              <span>
                                {needsGear(f) ? 'Needs gear' : 'No gear'}
                              </span>
                              {code && <span>First day: {code}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {!unassignedTeams.length && <p>No unassigned teams.</p>}
          </div>
        </section>
        <section className="load-order-grid">
          <div
            className="load-order-grid-header"
            style={{
              gridTemplateColumns: `80px repeat(${planes.length}, minmax(110px, 1fr))`,
            }}
          >
            <span>Load / Time</span>
            {planes.map((p) => (
              <span
                key={`plane-header-${p.id}`}
                className={`load-order-plane-header load-order-plane-${p.id}-header`}
              >
                {p.label}
                <button
                  type="button"
                  className="load-order-plane-email-btn"
                  title="Early load email to all mentors on this plane"
                  onClick={() => handlePlaneEarlyWarning(p.id)}
                >
                  ✉
                </button>
              </span>
            ))}
          </div>
          <div className="load-order-grid-body" key={planeOrder.join('|')}>
            {gridLoads.map((row) => (
              <div
                key={row.loadIndex}
                className="load-order-grid-row"
                style={{
                  gridTemplateColumns: `80px repeat(${planes.length}, minmax(110px, 1fr))`,
                }}
              >
                <div className="load-order-time-cell">
                  Load {row.loadIndex}
                </div>
                {planes.map((p) => {
                  const slots = [];
                  for (let i = 1; i <= teamsPerLoad; i++) {
                    const key = `${p.id}:${row.loadIndex}:${i}`;
                    const team = teamsBySlot[key];
                    const isSwapTarget =
                      team &&
                      draggedItem?.teamId &&
                      team.id !== draggedItem.teamId;
                    const isEmptyDropTarget =
                      !team && Boolean(draggedItem?.teamId);
                    const isDragOver = isEmptyDropTarget || isSwapTarget;
                    slots.push(
                      <div
                        key={i}
                        className={
                          'load-order-slot' +
                          (team ? ' load-order-slot--filled' : ' load-order-slot--empty') +
                          (isDragOver ? ' drag-over' : '')
                        }
                        onDragOver={handleSlotDragOver}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleDropOnSlot(p.id, row.loadIndex, i);
                        }}
                      >
                        {i === 1 && row.timesByPlane[p.id] && (
                          <div style={{ fontSize: '0.7rem', marginBottom: '0.1rem', color: '#555' }}>
                            {row.timesByPlane[p.id]}
                          </div>
                        )}
                        {team && (
                          <div
                            className="load-order-slot-team"
                            draggable
                            onDragOver={handleSlotDragOver}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDropOnSlot(p.id, row.loadIndex, i);
                            }}
                            onDragStart={(e) => {
                              e.stopPropagation();
                              setDraggedItem({ teamId: team.id });
                              e.dataTransfer.setData('text/plain', team.id);
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => setDraggedItem(null)}
                            onClick={() =>
                              setExpandedSlotted((prev) =>
                                prev.includes(team.id)
                                  ? prev.filter((id) => id !== team.id)
                                  : [...prev, team.id]
                              )
                            }
                          >
                            <div className="load-order-slot-team-header">
                              <span className="load-order-slot-team-name">
                                {team.teamNumber
                                  ? `#${team.teamNumber} – `
                                  : ''}
                                {team.mentorFlyer
                                  ? getDisplayName(team.mentorFlyer)
                                  : 'No mentor'}
                                {getFreshlingSummary(team) && (
                                  <span className="load-order-slot-team-fresh">
                                    {' · '}
                                    {getFreshlingSummary(team)}
                                  </span>
                                )}
                                {team.mentorFlyer && team.mentorFlyer.email && (
                                  <button
                                    type="button"
                                    className="load-order-email-btn"
                                    draggable={false}
                                    title="Early load email to mentor"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTeamEarlyWarning(team);
                                    }}
                                  >
                                    ✉
                                  </button>
                                )}
                              </span>
                              <div className="load-order-slot-team-controls">
                                <input
                                  type="text"
                                  key={`${team.id}-${team.teamNumber || ''}`}
                                  defaultValue={team.teamNumber || ''}
                                  className="load-order-slot-team-number-input"
                                  onBlur={(e) =>
                                    handleInlineNumberChange(
                                      team.id,
                                      e.target.value
                                    )
                                  }
                                />
                                <button
                                  type="button"
                                  className="load-order-slot-team-remove"
                                  draggable={false}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFromSlot(team.id);
                                  }}
                                  title="Remove team from this slot"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                            <div className="load-order-slot-team-footer">
                              <div>
                                {team.earlyLoadWarningSentAt && (
                                  <span className="load-order-early-label">
                                    Early load warning sent
                                  </span>
                                )}
                                {team.mentorLoadSummarySentAt && (
                                  <span className="load-order-early-label">
                                    Mentor load summary sent
                                  </span>
                                )}
                              </div>
                            </div>
                            {expandedSlotted.includes(team.id) &&
                              team.freshlingFlyers.length > 0 && (
                                <div className="load-order-team-pill-details">
                                  {team.freshlingFlyers.map((f, idx) => {
                                    const { code } = firstDayInfo(f.days);
                                    return (
                                      <div
                                        key={f.email || idx}
                                        className="load-order-team-pill-detail-row"
                                      >
                                        <div className="load-order-team-pill-detail-name">
                                          {getDisplayName(f) || f.email || 'Freshling'}
                                        </div>
                                        <div className="load-order-team-pill-detail-meta">
                                          <span>{f.homeDz || 'Home DZ ?'}</span>
                                          <span>{abbreviateDays(f.days) || 'Days ?'}</span>
                                          <span>
                                            {needsGear(f) ? 'Needs gear' : 'No gear'}
                                          </span>
                                          {code && <span>First day: {code}</span>}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`plane-col-${p.id}`}
                      className={`load-order-plane-column load-order-plane-${p.id}-col`}
                    >
                      {slots}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="load-order-assign-button"
            onClick={handleAssignTeamNumbersFromLoadOrder}
            disabled={assigningNumbers}
          >
            {assigningNumbers ? 'Assigning numbers…' : 'Assign Team Numbers'}
          </button>
        </section>
      </div>
      {loadOrderTableExport && (
        <AdminColumnExportModal
          mode={loadOrderTableExport.mode}
          columns={LOAD_ORDER_EXPORT_COLS}
          selectedIds={loadOrderExportCols}
          onChangeSelected={setLoadOrderExportCols}
          onCancel={() => setLoadOrderTableExport(null)}
          onConfirm={runLoadOrderTableExport}
          onResetDefaults={resetLoadOrderExportCols}
        />
      )}
      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
        onEmailSent={handleEmailSent}
      />
    </div>
  );
}

export default LoadOrder;

